import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { Popover } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { ChevronRight, Check, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';
import { Caption } from '../typography/Typography';

// ============================================================================
// Types
// ============================================================================

export type DropdownMenuSide = 'top' | 'right' | 'bottom' | 'left';
export type DropdownMenuAlign = 'start' | 'center' | 'end';

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  closeOnSelect: boolean;
}

interface DropdownMenuSubContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface RadioGroupContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

// ============================================================================
// Contexts
// ============================================================================

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);
const DropdownMenuSubContext = createContext<DropdownMenuSubContextValue | null>(null);
const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

const useDropdownMenu = () => {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('DropdownMenu components must be used within a DropdownMenu');
  }
  return context;
};

const useDropdownMenuSub = () => {
  const context = useContext(DropdownMenuSubContext);
  return context;
};

const useRadioGroup = () => {
  const context = useContext(RadioGroupContext);
  return context;
};

// ============================================================================
// Utility Functions
// ============================================================================

const getAntPlacement = (side: DropdownMenuSide, align: DropdownMenuAlign): TooltipPlacement => {
  const placementMap: Record<string, TooltipPlacement> = {
    'top-start': 'topLeft',
    'top-center': 'top',
    'top-end': 'topRight',
    'right-start': 'rightTop',
    'right-center': 'right',
    'right-end': 'rightBottom',
    'bottom-start': 'bottomLeft',
    'bottom-center': 'bottom',
    'bottom-end': 'bottomRight',
    'left-start': 'leftTop',
    'left-center': 'left',
    'left-end': 'leftBottom',
  };
  return placementMap[`${side}-${align}`] || 'bottomLeft';
};

// ============================================================================
// Styles - Using Design System Colors (Memoized to prevent recreation)
// ============================================================================

const MENU_CONTENT_STYLES: React.CSSProperties = {
  backgroundColor: colors.white,
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
  minWidth: 180,
  padding: 4,
  overflow: 'hidden',
} as const;

const MENU_ITEM_BASE_STYLES: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: 4,
  fontSize: 14, // Matches body2 from Typography
  fontWeight: 400,
  lineHeight: 1.5,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  outline: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  backgroundColor: colors.transparent,
  color: colors.textDefault,
} as const;

const MENU_ITEM_HOVER_STYLES: React.CSSProperties = {
  backgroundColor: colors.surfaceLighter,
} as const;

const MENU_ITEM_DISABLED_STYLES: React.CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  pointerEvents: 'none',
  color: colors.textWeak,
} as const;

const MENU_ITEM_DESTRUCTIVE_STYLES: React.CSSProperties = {
  color: colors.error,
} as const;

// Indicator styles for checkbox and radio items
const INDICATOR_STYLES: React.CSSProperties = {
  position: 'absolute',
  left: 8,
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

// ============================================================================
// DropdownMenu Root
// ============================================================================

export interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Reserved for future use (portal behavior) */
  modal?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  }, [isControlled, onOpenChange]);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, closeOnSelect: true }}>
      {children}
    </DropdownMenuContext.Provider>
  );
};

// ============================================================================
// DropdownMenuTrigger
// ============================================================================

export interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

// Types for cloneElement with proper typing
interface TriggerChildProps {
  onClick?: (e: React.MouseEvent) => void;
  ref?: React.Ref<HTMLElement>;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'menu' | 'dialog' | 'listbox' | 'tree' | 'grid';
  className?: string;
}

export const DropdownMenuTrigger = React.forwardRef<HTMLElement, DropdownMenuTriggerProps>(
  ({ children, asChild, className }, ref) => {
    const { setOpen, open } = useDropdownMenu();

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
      },
      [setOpen, open]
    );

    if (asChild && React.isValidElement<TriggerChildProps>(children)) {
      const childElement = children as React.ReactElement<TriggerChildProps>;
      // eslint-disable-next-line react-hooks/refs -- Passing ref through cloneElement is a valid forwardRef pattern
      return React.cloneElement(childElement, {
        onClick: handleClick,
        ref: ref,
        'aria-expanded': open,
        'aria-haspopup': 'menu',
        className: cn(childElement.props.className, className),
      });
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={handleClick}
        aria-expanded={open}
        aria-haspopup="menu"
        className={className}
        type="button"
      >
        {children}
      </button>
    );
  }
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

// ============================================================================
// DropdownMenuContent
// ============================================================================

export interface DropdownMenuContentProps {
  children: React.ReactNode;
  side?: DropdownMenuSide;
  align?: DropdownMenuAlign;
  sideOffset?: number;
  alignOffset?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = memo(
  ({
    children,
    side = 'bottom',
    align = 'start',
    className,
    style,
  }) => {
    const { open, setOpen } = useDropdownMenu();
    const placement = useMemo(() => getAntPlacement(side, align), [side, align]);

    const contentStyle = useMemo(
      () => ({ ...MENU_CONTENT_STYLES, ...style }),
      [style]
    );

    const handleClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <Popover
        open={open}
        onOpenChange={setOpen}
        placement={placement}
        trigger="click"
        arrow={false}
        overlayInnerStyle={{ padding: 0 }}
        overlayStyle={{ zIndex: 1050 }}
        content={
          <div
            role="menu"
            className={cn('dropdown-menu-content', className)}
            style={contentStyle}
            onClick={handleClick}
          >
            {children}
          </div>
        }
      >
        <span style={{ display: 'none' }} />
      </Popover>
    );
  }
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

// ============================================================================
// DropdownMenuPortal (for compatibility - renders children directly)
// ============================================================================

export const DropdownMenuPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// ============================================================================
// DropdownMenuGroup
// ============================================================================

export interface DropdownMenuGroupProps {
  children: React.ReactNode;
  className?: string;
}

export const DropdownMenuGroup: React.FC<DropdownMenuGroupProps> = memo(
  ({ children, className }) => {
    return (
      <div role="group" className={cn('dropdown-menu-group', className)}>
        {children}
      </div>
    );
  }
);

DropdownMenuGroup.displayName = 'DropdownMenuGroup';

// ============================================================================
// DropdownMenuLabel
// ============================================================================

export interface DropdownMenuLabelProps {
  children: React.ReactNode;
  inset?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const DropdownMenuLabel: React.FC<DropdownMenuLabelProps> = memo(
  ({
    children,
    inset,
    className,
    style,
  }) => {
    const labelStyles = useMemo<React.CSSProperties>(
      () => ({
        padding: '8px 12px',
        paddingLeft: inset ? 32 : 12,
        ...style,
      }),
      [inset, style]
    );

    return (
      <div className={cn('dropdown-menu-label', className)} style={labelStyles}>
        <Caption color="muted" weight={600} noMargin>
          {children}
        </Caption>
      </div>
    );
  }
);

DropdownMenuLabel.displayName = 'DropdownMenuLabel';

// ============================================================================
// DropdownMenuItem
// ============================================================================

export interface DropdownMenuItemProps {
  children: React.ReactNode;
  disabled?: boolean;
  onSelect?: (e: Event) => void;
  onClick?: (e: React.MouseEvent) => void;
  inset?: boolean;
  variant?: 'default' | 'destructive';
  className?: string;
  style?: React.CSSProperties;
}

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = memo(
  ({
    children,
    disabled = false,
    onSelect,
    onClick,
    inset,
    variant = 'default',
    className,
    style,
  }) => {
    const { setOpen, closeOnSelect } = useDropdownMenu();
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) return;
        onClick?.(e);
        onSelect?.(e.nativeEvent);
        if (closeOnSelect) {
          setOpen(false);
        }
      },
      [disabled, onClick, onSelect, closeOnSelect, setOpen]
    );

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const itemStyles = useMemo<React.CSSProperties>(
      () => ({
        ...MENU_ITEM_BASE_STYLES,
        ...(isHovered && !disabled ? MENU_ITEM_HOVER_STYLES : {}),
        ...(disabled ? MENU_ITEM_DISABLED_STYLES : {}),
        ...(variant === 'destructive' ? MENU_ITEM_DESTRUCTIVE_STYLES : {}),
        paddingLeft: inset ? 32 : 12,
        ...style,
      }),
      [isHovered, disabled, variant, inset, style]
    );

    return (
      <button
        role="menuitem"
        type="button"
        disabled={disabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn('dropdown-menu-item', className)}
        style={itemStyles}
      >
        {children}
      </button>
    );
  }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

// ============================================================================
// DropdownMenuCheckboxItem
// ============================================================================

export interface DropdownMenuCheckboxItemProps {
  children: React.ReactNode;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export const DropdownMenuCheckboxItem: React.FC<DropdownMenuCheckboxItemProps> = memo(
  ({
    children,
    checked = false,
    onCheckedChange,
    disabled = false,
    className,
  }) => {
    const { setOpen, closeOnSelect } = useDropdownMenu();
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = useCallback(() => {
      if (disabled) return;
      onCheckedChange?.(!checked);
      if (closeOnSelect) {
        setOpen(false);
      }
    }, [disabled, onCheckedChange, checked, closeOnSelect, setOpen]);

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const itemStyles = useMemo<React.CSSProperties>(
      () => ({
        ...MENU_ITEM_BASE_STYLES,
        ...(isHovered && !disabled ? MENU_ITEM_HOVER_STYLES : {}),
        ...(disabled ? MENU_ITEM_DISABLED_STYLES : {}),
        paddingLeft: 32,
      }),
      [isHovered, disabled]
    );

    return (
      <button
        role="menuitemcheckbox"
        type="button"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn('dropdown-menu-checkbox-item', className)}
        style={itemStyles}
      >
        <span style={INDICATOR_STYLES}>
          {checked && <Check size={14} style={{ color: colors.primary }} />}
        </span>
        {children}
      </button>
    );
  }
);

DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

// ============================================================================
// DropdownMenuRadioGroup
// ============================================================================

export interface DropdownMenuRadioGroupProps {
  children: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export const DropdownMenuRadioGroup: React.FC<DropdownMenuRadioGroupProps> = ({
  children,
  value = '',
  onValueChange,
  className,
}) => {
  return (
    <RadioGroupContext.Provider value={{ value, onValueChange: onValueChange || (() => { }) }}>
      <div role="group" className={cn('dropdown-menu-radio-group', className)}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};

// ============================================================================
// DropdownMenuRadioItem
// ============================================================================

export interface DropdownMenuRadioItemProps {
  children: React.ReactNode;
  value: string;
  disabled?: boolean;
  className?: string;
}

export const DropdownMenuRadioItem: React.FC<DropdownMenuRadioItemProps> = memo(
  ({
    children,
    value,
    disabled = false,
    className,
  }) => {
    const { setOpen, closeOnSelect } = useDropdownMenu();
    const radioGroup = useRadioGroup();
    const [isHovered, setIsHovered] = useState(false);
    const isSelected = radioGroup?.value === value;

    const handleClick = useCallback(() => {
      if (disabled) return;
      radioGroup?.onValueChange(value);
      if (closeOnSelect) {
        setOpen(false);
      }
    }, [disabled, radioGroup, value, closeOnSelect, setOpen]);

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const itemStyles = useMemo<React.CSSProperties>(
      () => ({
        ...MENU_ITEM_BASE_STYLES,
        ...(isHovered && !disabled ? MENU_ITEM_HOVER_STYLES : {}),
        ...(disabled ? MENU_ITEM_DISABLED_STYLES : {}),
        paddingLeft: 32,
      }),
      [isHovered, disabled]
    );

    return (
      <button
        role="menuitemradio"
        type="button"
        aria-checked={isSelected}
        disabled={disabled}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn('dropdown-menu-radio-item', className)}
        style={itemStyles}
      >
        <span style={INDICATOR_STYLES}>
          {isSelected && (
            <Circle size={8} fill={colors.primary} style={{ color: colors.primary }} />
          )}
        </span>
        {children}
      </button>
    );
  }
);

DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

// ============================================================================
// DropdownMenuSeparator
// ============================================================================

export interface DropdownMenuSeparatorProps {
  className?: string;
}

const SEPARATOR_STYLES: React.CSSProperties = {
  height: 1,
  backgroundColor: colors.surfaceSubtle,
  margin: '4px -4px',
} as const;

export const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = memo(
  ({ className }) => {
    return (
      <div
        role="separator"
        className={cn('dropdown-menu-separator', className)}
        style={SEPARATOR_STYLES}
      />
    );
  }
);

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

// ============================================================================
// DropdownMenuShortcut
// ============================================================================

export interface DropdownMenuShortcutProps {
  children: React.ReactNode;
  className?: string;
}

const SHORTCUT_STYLES: React.CSSProperties = { marginLeft: 'auto' } as const;

export const DropdownMenuShortcut: React.FC<DropdownMenuShortcutProps> = memo(
  ({
    children,
    className,
  }) => {
    return (
      <span
        className={cn('dropdown-menu-shortcut tracking-wider', className)}
        style={SHORTCUT_STYLES}
      >
        <Caption color="muted" noMargin>
          {children}
        </Caption>
      </span>
    );
  }
);

DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// ============================================================================
// DropdownMenuSub
// ============================================================================

export interface DropdownMenuSubProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DropdownMenuSub: React.FC<DropdownMenuSubProps> = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  }, [isControlled, onOpenChange]);

  return (
    <DropdownMenuSubContext.Provider value={{ open, setOpen }}>
      {children}
    </DropdownMenuSubContext.Provider>
  );
};

// ============================================================================
// DropdownMenuSubTrigger
// ============================================================================

export interface DropdownMenuSubTriggerProps {
  children: React.ReactNode;
  disabled?: boolean;
  inset?: boolean;
  className?: string;
}

const SUB_TRIGGER_CONTENT_STYLES: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
} as const;

export const DropdownMenuSubTrigger: React.FC<DropdownMenuSubTriggerProps> = memo(
  ({
    children,
    disabled = false,
    inset,
    className,
  }) => {
    const subContext = useDropdownMenuSub();
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = useCallback(() => {
      if (!disabled) {
        setIsHovered(true);
        subContext?.setOpen(true);
      }
    }, [disabled, subContext]);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    const itemStyles = useMemo<React.CSSProperties>(
      () => ({
        ...MENU_ITEM_BASE_STYLES,
        ...(isHovered && !disabled ? MENU_ITEM_HOVER_STYLES : {}),
        ...(disabled ? MENU_ITEM_DISABLED_STYLES : {}),
        paddingLeft: inset ? 32 : 12,
        justifyContent: 'space-between',
      }),
      [isHovered, disabled, inset]
    );

    return (
      <button
        type="button"
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn('dropdown-menu-sub-trigger', className)}
        style={itemStyles}
      >
        <span style={SUB_TRIGGER_CONTENT_STYLES}>{children}</span>
        <ChevronRight size={14} style={{ color: colors.textMuted }} />
      </button>
    );
  }
);

DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

// ============================================================================
// DropdownMenuSubContent
// ============================================================================

export interface DropdownMenuSubContentProps {
  children: React.ReactNode;
  className?: string;
  sideOffset?: number;
}

export const DropdownMenuSubContent: React.FC<DropdownMenuSubContentProps> = memo(
  ({
    children,
    className,
    sideOffset = 2,
  }) => {
    const subContext = useDropdownMenuSub();

    const handleMouseEnter = useCallback(() => {
      subContext?.setOpen(true);
    }, [subContext]);

    const handleMouseLeave = useCallback(() => {
      subContext?.setOpen(false);
    }, [subContext]);

    const contentStyles = useMemo<React.CSSProperties>(
      () => ({
        ...MENU_CONTENT_STYLES,
        position: 'absolute',
        left: '100%',
        top: 0,
        marginLeft: sideOffset,
      }),
      [sideOffset]
    );

    if (!subContext?.open) return null;

    return (
      <div
        role="menu"
        className={cn('dropdown-menu-sub-content', className)}
        style={contentStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    );
  }
);

DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

// ============================================================================
// Combined Component for Easy Use
// ============================================================================

export interface DropdownMenuCombinedProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  side?: DropdownMenuSide;
  align?: DropdownMenuAlign;
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
}

// Styles for Popover body - extracted to prevent recreation
const POPOVER_BODY_STYLES = { padding: 0 } as const;
const POPOVER_OVERLAY_STYLES = { zIndex: 1050 } as const;

export const DropdownMenuCombined: React.FC<DropdownMenuCombinedProps> = memo(
  ({
    trigger,
    children,
    side = 'bottom',
    align = 'start',
    open,
    onOpenChange,
    className,
    contentClassName,
  }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;

    const placement = useMemo(() => getAntPlacement(side, align), [side, align]);

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        if (!isControlled) {
          setInternalOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      },
      [isControlled, onOpenChange]
    );

    const contextValue = useMemo<DropdownMenuContextValue>(
      () => ({
        open: isOpen,
        setOpen: handleOpenChange,
        closeOnSelect: true,
      }),
      [isOpen, handleOpenChange]
    );

    const menuContent = useMemo(
      () => (
        <DropdownMenuContext.Provider value={contextValue}>
          <div
            role="menu"
            className={cn('dropdown-menu-content', contentClassName)}
            style={MENU_CONTENT_STYLES}
          >
            {children}
          </div>
        </DropdownMenuContext.Provider>
      ),
      [contextValue, contentClassName, children]
    );

    return (
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        placement={placement}
        trigger="click"
        arrow={false}
        styles={{ body: POPOVER_BODY_STYLES }}
        overlayStyle={POPOVER_OVERLAY_STYLES}
        content={menuContent}
      >
        <span className={cn('inline-block', className)}>{trigger}</span>
      </Popover>
    );
  }
);

DropdownMenuCombined.displayName = 'DropdownMenuCombined';

export default DropdownMenu;
